import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PLATFORM_LIMITS } from "@cp/core";
import { api } from "../lib/api.js";
import { navigate } from "../lib/router.js";

interface UploadedMedia {
  id: string;
  kind: "image" | "video";
  url: string;
}

async function uploadFile(file: File): Promise<UploadedMedia> {
  const kind: "image" | "video" = file.type.startsWith("video") ? "video" : "image";
  const created = await api.createMedia({ filename: file.name, mime: file.type, bytes: file.size, kind });
  const put = await fetch(created.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
  if (!put.ok) throw new Error("Upload to storage failed");
  const done = await api.completeMedia(created.id, {});
  return { id: created.id, kind, url: done.previewUrl };
}

export function ComposeView() {
  const qc = useQueryClient();
  const connections = useQuery({ queryKey: ["connections"], queryFn: api.connections });
  const [caption, setCaption] = useState("");
  const [media, setMedia] = useState<UploadedMedia[]>([]);
  const [targetIds, setTargetIds] = useState<Set<string>>(new Set());
  const [when, setWhen] = useState("");
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: uploadFile,
    onSuccess: (m) => setMedia((prev) => [...prev, m]),
    onError: (e) => setError(String(e)),
  });

  const submit = useMutation({
    mutationFn: async (publishNow: boolean) => {
      setError(null);
      return api.createPost(
        {
          caption,
          mediaAssetIds: media.map((m) => m.id),
          targets: [...targetIds].map((connectionId) => ({ connectionId, options: {} })),
          scheduledAt: publishNow ? null : when ? new Date(when).toISOString() : null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          status: publishNow ? "draft" : "scheduled",
        },
        publishNow,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["posts"] });
      navigate("/posts");
    },
    onError: (e) => setError(String(e)),
  });

  const activeConns = (connections.data?.items ?? []).filter((c) => c.status === "active");

  return (
    <div>
      <h1>Compose</h1>

      <label>Caption</label>
      <textarea rows={5} value={caption} onChange={(e) => setCaption(e.target.value)} />
      <p className="muted">
        {[...targetIds]
          .map((id) => activeConns.find((c) => c.id === id)?.platform)
          .filter(Boolean)
          .map((p) => `${p}: ${caption.length}/${PLATFORM_LIMITS[p as keyof typeof PLATFORM_LIMITS].captionMax}`)
          .join("  ·  ")}
      </p>

      <label>Media</label>
      <input
        type="file"
        accept="image/*,video/*"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
        }}
      />
      {upload.isPending && <p className="muted">Uploading…</p>}
      <div className="row" style={{ flexWrap: "wrap", marginTop: "0.5rem" }}>
        {media.map((m) => (
          <div key={m.id} className="card" style={{ width: 120 }}>
            {m.kind === "image" ? (
              <img src={m.url} style={{ width: "100%", borderRadius: 6 }} />
            ) : (
              <video src={m.url} style={{ width: "100%" }} muted />
            )}
          </div>
        ))}
      </div>

      <label>Publish to</label>
      {activeConns.length === 0 && <p className="muted">No active connections — add one first.</p>}
      {activeConns.map((c) => (
        <label key={c.id} className="row" style={{ margin: "0.3rem 0" }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={targetIds.has(c.id)}
            onChange={(e) => {
              const next = new Set(targetIds);
              e.target.checked ? next.add(c.id) : next.delete(c.id);
              setTargetIds(next);
            }}
          />
          <span>
            {c.displayName} <span className="badge">{c.platform}</span>
          </span>
        </label>
      ))}

      <label>Schedule for</label>
      <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />

      {error && <p style={{ color: "crimson" }}>{error}</p>}

      <div className="row" style={{ marginTop: "1rem" }}>
        <button disabled={submit.isPending || targetIds.size === 0 || !when} onClick={() => submit.mutate(false)}>
          Schedule
        </button>
        <button disabled={submit.isPending || targetIds.size === 0} onClick={() => submit.mutate(true)}>
          Publish now
        </button>
      </div>
    </div>
  );
}
